import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  MagnifyingGlass, ArrowRight, ArrowUpRight, MapPin, GraduationCap,
  UsersThree, Buildings, HandHeart, Flower, Translate, Star, EnvelopeSimple, Check,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EDITION } from "@/lib/edition";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GREETINGS = ["नमस्ते", "வணக்கம்", "নমস্কার", "నమస్కారం", "ನಮಸ್ಕಾರ", "നമസ്കാരം", "નમસ્તે", "ਸਤ ਸ੍ਰੀ ਅਕਾਲ", "নমস্কাৰ"];

const LANGS = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिन्दी" },
  { code: "ml", label: "മലയാളം" },
  { code: "ta", label: "தமிழ்" },
  { code: "te", label: "తెలుగు" },
  { code: "bn", label: "বাংলা" },
];

const ICONIC = [
  "IIT", "IIM", "University of Delhi", "JNU", "BHU", "Anna University",
  "Jadavpur University", "University of Mumbai", "Osmania University",
  "AIIMS", "NIT", "St. Stephen's College", "Loyola College", "Presidency University",
];

const STATE_META = {
  "Kerala": { hue: "from-emerald-600 to-teal-700" },
  "Uttar Pradesh": { hue: "from-orange-600 to-amber-700" },
  "Maharashtra": { hue: "from-indigo-700 to-violet-800" },
  "Tamil Nadu": { hue: "from-rose-600 to-red-700" },
  "Karnataka": { hue: "from-amber-600 to-orange-700" },
  "West Bengal": { hue: "from-red-700 to-rose-800" },
  "Rajasthan": { hue: "from-pink-600 to-fuchsia-700" },
  "Gujarat": { hue: "from-teal-600 to-cyan-700" },
  "Madhya Pradesh": { hue: "from-purple-700 to-indigo-800" },
  "Bihar": { hue: "from-lime-600 to-green-700" },
  "Telangana": { hue: "from-cyan-700 to-sky-800" },
  "Andhra Pradesh": { hue: "from-sky-600 to-blue-700" },
};

// English source strings sent to the AI translator (order-independent; keyed by value).
const HERO_SUB = "From village schools to the IITs — {n}+ Indian institutions, their alumni communities, teachers and memories, all in one place. Find your batch. Rebuild your circle.";
const I18N = [
  "Reconnect with your Indian alma mater.", HERO_SUB,
  "Search IITs, DU, regional colleges & schools…", "Search",
  "Join your alumni group", "Create your profile", EDITION.other.note,
  "institutions across India", "Institutions", "States & UTs", "Alumni communities", "Regional languages",
  "Alumni from every corner of India", "Browse by state", "Find your home state", "All states & UTs", "institutions",
  "In your language", "Every institution, in your mother tongue.",
  "Read institution pages and alumni discussions in Malayalam, Hindi, Tamil, Telugu, Bengali, Kannada and more — translated instantly so no one is left out of the conversation.",
  "Your batch is waiting.",
  "Every institution has an alumni community and class-of-year boards. Join, share memories, and plan your next reunion.",
  "Explore alumni groups", "Honouring the teachers who shaped us.",
  "Permanent profiles and eternal memorials for the gurus, faculty and classmates we've lost — a lasting place to say \"this person taught me\".",
  "In Memoriam", "Find my teacher", "Your alma mater still needs you.",
  "From scholarships to labs to mentoring — give back to the school or college that made you. IndiaAlumni connects the need with the alumni who can help.",
  "See how alumni are giving back",
  "Get weekly India updates", "New institutions, alumni groups and reunions added across India — straight to your inbox.",
  "Your email address", "Subscribe", "Subscribing…", "Subscribed!",
];

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.06 } }),
};

export default function IndiaLanding() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [states, setStates] = useState([]);
  const [total, setTotal] = useState(0);
  const [obitRegions, setObitRegions] = useState([]);
  const [lang, setLang] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search).get("lang");
      if (p) return p;
      return localStorage.getItem("ia_lang") || "en";
    } catch { return "en"; }
  });
  const [tr, setTr] = useState({});

  useEffect(() => {
    axios.get(`${API}/institutions/locations`, { params: { country: "India" } })
      .then(({ data }) => {
        const india = (data || []).find((c) => c.country === "India") || (data && data[0]);
        const st = (india?.states || []).filter((s) => s.state && s.state !== "Unknown");
        st.sort((a, b) => (b.count || 0) - (a.count || 0));
        setStates(st);
        setTotal(st.reduce((n, s) => n + (s.count || 0), 0));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${API}/public/obituaries/geo?country=India`)
      .then(({ data }) => setObitRegions(data.options || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    try { localStorage.setItem("ia_lang", lang); } catch {}
    if (lang === "en") { setTr({}); return; }
    const cacheKey = "ia_tr_" + lang;
    try { const c = localStorage.getItem(cacheKey); if (c) { setTr(JSON.parse(c)); return; } } catch {}
    axios.post(`${API}/i18n/translate`, { lang, texts: I18N })
      .then(({ data }) => {
        const arr = data.translations || [];
        const map = {};
        let changed = false;
        I18N.forEach((s, i) => { map[s] = arr[i] || s; if (arr[i] && arr[i] !== s) changed = true; });
        if (!changed) return; // translation unavailable — keep English, don't cache
        setTr(map);
        try { localStorage.setItem(cacheKey, JSON.stringify(map)); } catch {}
      })
      .catch(() => {});
  }, [lang]);

  const t = (s) => tr[s] || s;
  const topStates = useMemo(() => states.slice(0, 10), [states]);
  const search = (e) => {
    e?.preventDefault();
    navigate(`/explore/institutions?country=India${q.trim() ? `&q=${encodeURIComponent(q.trim())}` : ""}`);
  };
  const nf = (n) => (n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : `${n}`);

  return (
    <div data-testid="india-landing" className="overflow-x-hidden">
      {/* HERO */}
      <section className="relative isolate">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 py-16 lg:grid-cols-2 lg:py-24">
          <motion.div variants={fade} initial="hidden" animate="show">
            {/* Language switcher */}
            <div className="mb-5 flex flex-wrap items-center gap-1.5" data-testid="lang-switcher">
              <Translate size={16} className="mr-1 text-primary" weight="duotone" />
              {LANGS.map((l) => (
                <button key={l.code} onClick={() => setLang(l.code)} data-testid={`lang-${l.code}`}
                  className={`rounded-full px-3 py-1 text-sm transition-colors ${lang === l.code ? "bg-primary text-primary-foreground" : "border border-border/70 bg-white text-foreground/70 hover:border-primary/40"}`}>
                  {l.label}
                </button>
              ))}
            </div>
            <div className="mb-5 flex flex-wrap items-center gap-x-3 gap-y-1 overflow-hidden text-lg font-medium text-primary/80" data-testid="hero-greetings">
              {GREETINGS.map((g, i) => (
                <motion.span key={g} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 + i * 0.12 }}>
                  {g}{i < GREETINGS.length - 1 && <span className="mx-1 text-border">·</span>}
                </motion.span>
              ))}
            </div>
            <h1 className="font-heading text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
              {lang === "en"
                ? <>Reconnect with your <span className="text-primary">Indian</span> alma mater.</>
                : t("Reconnect with your Indian alma mater.")}
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground">
              {t(HERO_SUB).replace("{n}", nf(total || 83000))}
            </p>

            <form onSubmit={search} className="mt-8 flex max-w-xl flex-col gap-2 sm:flex-row" data-testid="hero-search-form">
              <div className="relative flex-1">
                <MagnifyingGlass className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="hero-search-input"
                  placeholder={t("Search IITs, DU, regional colleges & schools…")}
                  className="h-12 rounded-full border-border/70 bg-white pl-11 text-base shadow-sm" />
              </div>
              <Button type="submit" size="lg" className="h-12 gap-2 rounded-full px-6" data-testid="hero-search-btn">
                {t("Search")} <ArrowRight size={18} />
              </Button>
            </form>

            <div className="mt-5 flex flex-wrap items-center gap-3 text-sm">
              <Link to="/alumni-groups"><Button variant="outline" size="sm" className="gap-1.5 rounded-full" data-testid="hero-groups-btn"><UsersThree size={16} /> {t("Join your alumni group")}</Button></Link>
              <Link to="/register"><Button variant="ghost" size="sm" className="gap-1.5 rounded-full" data-testid="hero-signup-btn"><GraduationCap size={16} /> {t("Create your profile")}</Button></Link>
            </div>

            <a href={EDITION.other.url} target="_blank" rel="noreferrer" data-testid="global-link-hero"
              className="mt-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-secondary">
              {t(EDITION.other.note)} <span className="font-medium text-secondary">{EDITION.other.label}</span>
              <ArrowUpRight size={14} />
            </a>
          </motion.div>

          <motion.div variants={fade} initial="hidden" animate="show" custom={2} className="relative">
            <div className="relative overflow-hidden rounded-[2rem] border border-border/50 shadow-2xl shadow-accent/10">
              <img src="/indiaalumni-hero.jpg" alt="Indian campus heritage" className="h-[360px] w-full object-cover sm:h-[460px]" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A243B]/55 via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-5 -left-5 hidden rounded-2xl border border-border/60 bg-white/90 px-5 py-4 shadow-xl backdrop-blur sm:block">
              <p className="font-heading text-2xl font-bold text-primary">{nf(total)}+</p>
              <p className="text-xs text-muted-foreground">{t("institutions across India")}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* STATS BAND */}
      <section className="border-y border-border/60 bg-muted/40">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-6 px-5 py-8 sm:grid-cols-4">
          {[
            { icon: Buildings, val: `${nf(total)}+`, label: "Institutions" },
            { icon: MapPin, val: `${states.length || 36}`, label: "States & UTs" },
            { icon: UsersThree, val: `${nf(total)}+`, label: "Alumni communities" },
            { icon: Translate, val: "12+", label: "Regional languages" },
          ].map((s, i) => (
            <motion.div key={s.label} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
              className="flex items-center gap-3" data-testid={`stat-${i}`}>
              <div className="grid h-11 w-11 place-items-center rounded-2xl bg-primary/10 text-primary"><s.icon size={22} weight="duotone" /></div>
              <div>
                <p className="font-heading text-xl font-bold leading-none">{s.val}</p>
                <p className="text-xs text-muted-foreground">{t(s.label)}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ICONIC MARQUEE */}
      <section className="overflow-hidden py-10">
        <p className="mb-5 text-center text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{t("Alumni from every corner of India")}</p>
        <div className="relative flex overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)]">
          <div className="flex shrink-0 animate-[marquee_38s_linear_infinite] gap-3 pr-3">
            {[...ICONIC, ...ICONIC].map((name, i) => (
              <button key={i} onClick={() => navigate(`/explore/institutions?country=India&q=${encodeURIComponent(name)}`)}
                className="whitespace-nowrap rounded-full border border-border/60 bg-white px-4 py-2 text-sm font-medium text-foreground/80 transition-colors hover:border-primary/50 hover:text-primary">
                {name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* BROWSE BY STATE — bento */}
      <section className="mx-auto max-w-7xl px-5 py-14">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">{t("Browse by state")}</p>
            <h2 className="mt-2 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">{t("Find your home state")}</h2>
          </div>
          <Link to="/countries/India" className="inline-flex items-center gap-1 text-sm font-medium text-secondary hover:underline" data-testid="all-states-link">
            {t("All states & UTs")} <ArrowRight size={15} />
          </Link>
        </div>

        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {topStates.map((s, i) => {
            const meta = STATE_META[s.state] || { hue: "from-slate-600 to-slate-800" };
            const big = i === 0;
            return (
              <motion.button
                key={s.state}
                variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                onClick={() => navigate(`/countries/India?state=${encodeURIComponent(s.state)}`)}
                data-testid={`state-bento-card-${s.state.toLowerCase().replace(/\s+/g, "-")}`}
                className={`group relative overflow-hidden rounded-3xl border border-border/60 p-5 text-left transition-transform duration-200 hover:-translate-y-1 hover:border-primary/40 ${big ? "col-span-2 row-span-2 md:col-span-2" : ""}`}>
                <div className={`absolute inset-0 bg-gradient-to-br ${meta.hue} opacity-[0.92]`} />
                <div className="absolute inset-0 opacity-20 [background-image:radial-gradient(circle_at_1px_1px,white_1px,transparent_0)] [background-size:14px_14px]" />
                <div className="relative flex h-full flex-col justify-between text-white">
                  <MapPin size={big ? 28 : 20} weight="duotone" className="opacity-90" />
                  <div className={big ? "mt-16" : "mt-8"}>
                    <p className={`font-heading font-semibold leading-tight ${big ? "text-2xl" : "text-base"}`}>{s.state}</p>
                    <p className={`mt-0.5 font-medium text-white/85 ${big ? "text-sm" : "text-xs"}`}>{nf(s.count)} {t("institutions")}</p>
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>
      </section>

      {/* REGIONAL LANGUAGES + GROUPS */}
      <section className="border-y border-border/60 bg-muted/30 py-14">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 lg:grid-cols-2">
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }}>
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary"><Translate size={14} /> {t("In your language")}</div>
            <h2 className="font-heading text-3xl font-semibold tracking-tight sm:text-4xl">{t("Every institution, in your mother tongue.")}</h2>
            <p className="mt-4 max-w-lg text-base leading-relaxed text-muted-foreground">
              {t("Read institution pages and alumni discussions in Malayalam, Hindi, Tamil, Telugu, Bengali, Kannada and more — translated instantly so no one is left out of the conversation.")}
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["മലയാളം", "हिन्दी", "தமிழ்", "తెలుగు", "বাংলা", "ಕನ್ನಡ", "मराठी", "ગુજરાતી"].map((l) => (
                <span key={l} className="rounded-full border border-border/70 bg-white px-3 py-1.5 text-sm">{l}</span>
              ))}
            </div>
          </motion.div>
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1}
            className="rounded-3xl border border-border/60 bg-white p-8 shadow-sm">
            <UsersThree size={40} weight="duotone" className="text-secondary" />
            <h3 className="mt-4 font-heading text-2xl font-semibold">{t("Your batch is waiting.")}</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {t("Every institution has an alumni community and class-of-year boards. Join, share memories, and plan your next reunion.")}
            </p>
            <Link to="/alumni-groups"><Button className="mt-5 gap-1.5 rounded-full" data-testid="groups-cta"><UsersThree size={16} /> {t("Explore alumni groups")}</Button></Link>
          </motion.div>
        </div>
      </section>

      {/* LEGACY / IN MEMORIAM */}
      <section className="mx-auto max-w-5xl px-5 py-16 text-center">
        <Flower size={36} weight="duotone" className="mx-auto text-secondary" />
        <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">{t("Honouring the teachers who shaped us.")}</h2>
        <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
          {t("Permanent profiles and eternal memorials for the gurus, faculty and classmates we've lost — a lasting place to say \"this person taught me\".")}
        </p>
        <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
          <Link to="/memorials"><Button variant="outline" className="gap-1.5 rounded-full" data-testid="memorials-cta"><Flower size={16} /> {t("In Memoriam")}</Button></Link>
          <Link to="/find-teacher"><Button variant="ghost" className="gap-1.5 rounded-full" data-testid="find-teacher-cta"><Star size={16} /> {t("Find my teacher")}</Button></Link>
        </div>
        {obitRegions.length > 0 && (
          <div className="mt-10" data-testid="in-memoriam-by-region">
            <p className="text-sm font-medium text-muted-foreground">{t("In Memoriam — remembered across India")}</p>
            <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
              {obitRegions.slice(0, 12).map((r) => (
                <Link key={r.name} to="/memorials" data-testid={`in-memoriam-region-${r.name}`}
                  className="flex items-center gap-1.5 rounded-full border border-border/60 bg-white px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:-translate-y-0.5 hover:border-secondary hover:text-secondary">
                  <Flower size={13} weight="duotone" className="text-secondary" /> {r.name} <span className="opacity-70">{r.count}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* WEEKLY INDIA UPDATES — digest signup */}
      <DigestSignup t={t} />

      {/* IMPACT CTA — indigo */}
      <section className="relative overflow-hidden bg-[#1A243B] py-16 text-white">
        <div className="absolute inset-0 opacity-[0.07] [background-image:radial-gradient(circle_at_1px_1px,white_1px,transparent_0)] [background-size:18px_18px]" />
        <div className="relative mx-auto max-w-4xl px-5 text-center">
          <HandHeart size={40} weight="duotone" className="mx-auto text-primary" />
          <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">{t("Your alma mater still needs you.")}</h2>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-white/80">
            {t("From scholarships to labs to mentoring — give back to the school or college that made you. IndiaAlumni connects the need with the alumni who can help.")}
          </p>
          <Link to="/impact"><Button size="lg" className="mt-7 gap-2 rounded-full px-7" data-testid="alumni-impact-cta"><HandHeart size={18} /> {t("See how alumni are giving back")}</Button></Link>
        </div>
      </section>
    </div>
  );
}

function DigestSignup({ t }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const subscribe = async (e) => {
    e?.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/news/subscribe`, { email: email.trim(), country: "India" });
      setDone(true);
      toast.success(data.already ? "You're already subscribed — welcome back!" : "Subscribed! Your weekly India update is on its way.");
    } catch (err) {
      toast.error("Couldn't subscribe right now — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mx-auto max-w-3xl px-5 py-6">
      <div className="rounded-3xl border border-primary/20 bg-primary/5 p-7 text-center sm:p-9" data-testid="india-digest">
        <EnvelopeSimple size={34} weight="duotone" className="mx-auto text-primary" />
        <h2 className="mt-3 font-heading text-2xl font-semibold tracking-tight sm:text-3xl">{t("Get weekly India updates")}</h2>
        <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-muted-foreground">
          {t("New institutions, alumni groups and reunions added across India — straight to your inbox.")}
        </p>
        {done ? (
          <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-primary" data-testid="digest-done">
            <Check size={16} weight="bold" /> {t("Subscribed!")}
          </div>
        ) : (
          <form onSubmit={subscribe} className="mx-auto mt-5 flex max-w-md flex-col gap-2 sm:flex-row">
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="digest-email"
              placeholder={t("Your email address")} className="h-11 rounded-full border-border/70 bg-white text-base" />
            <Button type="submit" disabled={busy} className="h-11 gap-1.5 rounded-full px-6" data-testid="digest-subscribe">
              <EnvelopeSimple size={15} /> {busy ? t("Subscribing…") : t("Subscribe")}
            </Button>
          </form>
        )}
      </div>
    </section>
  );
}
